import React, { useEffect, useRef, useState } from 'react';
import '../../styles/v2/tokens.css';
import '../../styles/v2/shell.css';
import config from '../../configLoader';
import { useSiteContext } from '../../SiteContext';
import { useAdminSession } from '../../hooks/useAdminSession';
import { usePayPeriods } from '../../hooks/usePayPeriods';
import ActionButton from '../ActionButton';
import SidebarV2 from './SidebarV2';
import TopBarV2 from './TopBarV2';
import CompanyLogo from '../CompanyLogo';
import { PortalProvider } from '../../Pages/v2/employeePortal/PortalContext';

const COLLAPSE_KEY = 'admin_collapsed_v2';

interface LayoutV2Props {
  children?: React.ReactNode;
}

/** Inner shell — only mounted when logged in, so usePayPeriods fetches post-auth. */
const ShellBody: React.FC<{
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSignOut: () => void;
  children?: React.ReactNode;
}> = ({ collapsed, onToggleCollapse, onSignOut, children }) => {
  const { page } = useSiteContext();
  const contentRef = useRef<HTMLDivElement>(null);

  // Pay-periods fetch + current-week auto-pick (shared hook, same as WeekSelector).
  usePayPeriods();

  // Reset content scroll to top whenever the active page changes.
  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [page]);

  return (
    <>
      <TopBarV2 onToggleCollapse={onToggleCollapse} onSignOut={onSignOut} />
      <div className="app-body">
        <SidebarV2
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          onSignOut={onSignOut}
        />
        <div className="v2-content" ref={contentRef}>
          <div className="v2-content-inner">{children}</div>
        </div>
      </div>
    </>
  );
};

/**
 * The Employee Time Portal shell (P-TAS-20 Phase 3): the same TopBar + Sidebar frame, but
 * NOTHING here calls /admin/* — no usePayPeriods, no GlobalEmployeeSearch, no GlobalWeekPill —
 * because an employee-only user is refused there. The PortalProvider loads the /portal data
 * the My Time pages share.
 */
const EmployeeShellBody: React.FC<{
  collapsed: boolean;
  onToggleCollapse: () => void;
  onSignOut: () => void;
  children?: React.ReactNode;
}> = ({ collapsed, onToggleCollapse, onSignOut, children }) => {
  const { page } = useSiteContext();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
  }, [page]);

  return (
    <PortalProvider>
      <TopBarV2 onToggleCollapse={onToggleCollapse} onSignOut={onSignOut} variant="employee" />
      <div className="app-body">
        <SidebarV2
          collapsed={collapsed}
          onToggleCollapse={onToggleCollapse}
          onSignOut={onSignOut}
        />
        <div className="v2-content" ref={contentRef}>
          <div className="v2-content-inner">{children}</div>
        </div>
      </div>
    </PortalProvider>
  );
};

const LayoutV2: React.FC<LayoutV2Props> = ({ children }) => {
  const { loggedIn, employeeOnly, loginError, isAuthenticated, handleLogin, handleLogout } = useAdminSession();
  // employeeMode = the tenant's portal is enabled, the user has employee access and the
  // employee experience is selected (forced for employee-only users).
  const { translator, userName, employeeMode } = useSiteContext();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(COLLAPSE_KEY, String(next));
      } catch {
        /* ignore storage failures */
      }
      return next;
    });
  };

  return (
    <div className={`appv2${collapsed ? ' collapsed' : ''}`}>
      {(employeeOnly || loggedIn) && employeeMode ? (
        <EmployeeShellBody
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onSignOut={handleLogout}
        >
          {children}
        </EmployeeShellBody>
      ) : employeeOnly ? (
        // Employee-only login (TAS-667) on a tenant without an enabled portal.
        <div className="v2-signin">
          <div className="v2-signin-card">
            <CompanyLogo src={config.logo} alt="Logo" />
            <h2>Employee time tracking is coming soon</h2>
            <p>
              {userName ? `Hi ${userName}. ` : ''}Your account is set up for employee access.
              Clocking in and out, timesheets and time off will be available here soon.
              Until then, keep tracking your time the way you do today.
            </p>
            <ActionButton
              title={translator.translate('SignOut')}
              onAction={handleLogout}
              isEnabled={true}
            />
          </div>
        </div>
      ) : !loggedIn ? (
        <div className="v2-signin">
          <div className="v2-signin-card">
            <CompanyLogo src={config.logo} alt="Logo" />
            <h2>{translator.translate('PleaseSignIn')}</h2>
            {/* Fixed-height slot, always mounted: a failed sign-in never resizes the card. */}
            <p
              className="v2-signin-error"
              role="alert"
              style={{ visibility: loginError ? 'visible' : 'hidden' }}
            >
              {loginError ?? ''}
            </p>
            <ActionButton
              title={translator.translate('SignIn')}
              onAction={handleLogin}
              isEnabled={!loggedIn}
            />
            <button
              type="button"
              className="v2-signin-switch"
              onClick={handleLogout}
              disabled={!(loginError && isAuthenticated)}
              style={{ visibility: loginError && isAuthenticated ? 'visible' : 'hidden' }}
            >
              Sign out to use a different account
            </button>
          </div>
        </div>
      ) : (
        <ShellBody
          collapsed={collapsed}
          onToggleCollapse={toggleCollapse}
          onSignOut={handleLogout}
        >
          {children}
        </ShellBody>
      )}
    </div>
  );
};

export default LayoutV2;
